/**
 * Product Specs Components
 */
import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { type FunctionComponent, type HTMLAttributes } from 'react';
import { OVERVIEW_SECTION_ID } from '../../../../constants/product';
import { LinkButton } from '../../../buttons/link-button/link-button.component';

/**
 * @typedef AttributeProps
 */
export type AttributeProps = {
	id: string;
	name: string;
	values: string;
	units: string | null;
	description: string | null;
	isHidden: boolean;
};

/**
 * @typedef ProductSpecsListProps
 */
export type ProductSpecsListProps = {
	specs: { name: string; attributes: AttributeProps[] }[];
	showHidden?: boolean;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Specs List (Specs)
 *
 * @param {ProductSpecsListProps} props
 */
export const ProductSpecsList: FunctionComponent<ProductSpecsListProps> = ({ className = '', specs, showHidden = false }) => (
	<ul className={`list ${className}`.trim()}>
		{specs.map(({ name, attributes }) => (
			<li key={kebabCase(name)} className="mh0 mb2 pa0">
				<h3>{name}</h3>
				<ProductAttributeList attributes={attributes} showHidden={showHidden} />
			</li>
		))}
	</ul>
);

/**
 * @typedef ProductAttributeListProps
 */
export type ProductAttributeListProps = {
	attributes: AttributeProps[];
	showHidden?: boolean;
	title?: string;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Attribute List
 *
 * @param {ProductAttributeListProps} props
 */
export const ProductAttributeList: FunctionComponent<ProductAttributeListProps> = ({
	className = 'w-100 f6',
	attributes,
	showHidden = false,
	title
}) => {
	const filtered = !showHidden ? attributes.filter(({ isHidden }) => !isHidden) : attributes;
	return filtered.length > 0 ? (
		<>
			{title && <h3 className="mt3 mb0">{title}</h3>}
			<ul className={`flex flex-column items-start ma0 ${className}`.trim()}>
				{filtered.map(({ id, name, values, units }) => (
					<li key={`${id}-${name}`} className="tl mh0 mv1 pa0">
						<span className="b">{name}:</span>
						<span className="ml2">
							{values} {units}
						</span>
					</li>
				))}
			</ul>
		</>
	) : null;
};

export type ProductKeySpecsProps = {
	id: number;
	url: string;
	testId?: string;
	showMoreDetailsButton?: boolean;
} & ProductAttributeListProps;

/**
 * Product Key Specs
 *
 * @param {ProductKeySpecsProps} props
 */
export const ProductKeySpecs: FunctionComponent<ProductKeySpecsProps> = ({
	className = '',
	id,
	url,
	title = 'Key Specifications',
	attributes,
	showHidden = false,
	testId = 'Key Specs',
	showMoreDetailsButton = true
}) => (
	<div className={className} data-testid={testId}>
		<div className="b pb2">{title}</div>
		<div className={`flex flex-column flex-row-ns pv2`}>
			<div className="flex flex-row">
				<ProductAttributeList attributes={attributes.slice(0, Math.ceil(attributes.length / 2))} showHidden={showHidden} />
			</div>
			<div className="flex flex-row">
				<ProductAttributeList
					attributes={attributes.slice(Math.ceil(attributes.length / 2), attributes.length)}
					showHidden={showHidden}
				/>
			</div>
		</div>
		{showMoreDetailsButton && (
			<div className="mt3">
				<LinkButton size="SMALL" buttonType="button" buttonStyle="SECONDARY" url={`${url}?uid=${id}#${OVERVIEW_SECTION_ID}`}>
					See More Details
				</LinkButton>
			</div>
		)}
	</div>
);
